import React from 'react';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/admin/components/ui/hover-card';
import { cn } from '@/admin/lib/utils';
import type { Skill } from '@/admin/lib/api';

interface SkillSelectorWithPreviewProps {
  skills: Skill[];
  selectedIds: string[];
  onToggle: (skillId: string) => void;
  loading?: boolean;
  className?: string;
}

function SkillPreviewContent({ skill }: { skill: Skill }) {
  return (
    <div className="space-y-3">
      {/* Cover Image */}
      {skill.hero_url && (
        <div className="relative aspect-video rounded-md overflow-hidden bg-muted -mx-1 -mt-1">
          <img
            src={skill.hero_url}
            alt={skill.display_name || skill.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Info */}
      <div className="space-y-1.5">
        <h4 className="font-medium text-sm">
          {skill.display_name || skill.name}
        </h4>
        {skill.description && (
          <p className="text-xs text-muted-foreground line-clamp-3">
            {skill.description}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {skill.category && (
            <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">
              {skill.category}
            </span>
          )}
          {skill.status && (
            <span
              className={cn(
                'px-1.5 py-0.5 rounded text-[10px]',
                skill.status === 'published'
                  ? 'bg-emerald-500/10 text-emerald-600'
                  : 'bg-amber-500/10 text-amber-600'
              )}
            >
              {skill.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SkillButton({
  skill,
  isSelected,
  onToggle,
}: {
  skill: Skill;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border-2 transition-colors',
            isSelected
              ? 'bg-primary/10 text-primary border-primary'
              : 'bg-background text-muted-foreground border-transparent hover:border-muted hover:text-foreground'
          )}
        >
          {skill.display_name || skill.name}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        className="w-72 p-3"
        sideOffset={8}
      >
        <SkillPreviewContent skill={skill} />
      </HoverCardContent>
    </HoverCard>
  );
}

export const SkillSelectorWithPreview: React.FC<
  SkillSelectorWithPreviewProps
> = ({ skills, selectedIds, onToggle, loading = false, className }) => {
  if (loading) {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 inline-block mr-2" />
        ))}
      </div>
    );
  }

  if (skills.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No skills available.</p>
    );
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {skills.map((skill) => (
        <SkillButton
          key={skill.id}
          skill={skill}
          isSelected={selectedIds.includes(skill.id)}
          onToggle={() => onToggle(skill.id)}
        />
      ))}
    </div>
  );
};
